// src/components/admin/InvoiceManagement.js
import { format } from 'date-fns';
import {
  AlertCircle,
  CheckCircle,
  ChevronLeft, ChevronRight,
  DollarSign,
  Download,
  ExternalLink,
  FileText,
  Filter,
  Mail,
  Plus,
  Search,
  Tag,
  Trash,
  User,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../../context/AuthContext";
import axios from '../../utils/axiosHandler';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';


const InvoiceManagement = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const { user } = useAuth();
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    clientId: '',
    startDate: '',
    endDate: '',
    isPaid: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareEmail, setShareEmail] = useState({ to: '', message: '' });
  const [shareSMS, setShareSMS] = useState({ to: '', message: '' });
  const [shareMode, setShareMode] = useState('email'); // 'email' or 'sms'
  const [sendingShare, setSendingShare] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Create invoice form state
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [createdInvoice, setCreatedInvoice] = useState(null);

  const [invoiceData, setInvoiceData] = useState({
    client: '',
    dueDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'), // 30 days from now
    items: [{ product: '', quantity: 1, unitPrice: 0, description: '', hasGST: false, hasPST: false }],
    customFields: [],
    notes: '',
    terms: 'Payment is due within 30 days of receipt.',
    subtotal: 0,
    gstTotal: 0,
    pstTotal: 0,
    discountAmount: 0,
    totalAmount: 0,
    coupon: null,
    companyInfo: {
      name: '',
      address: '',
      taxNumber: '',
      email: '',
      phone: ''
    }
  });

  useEffect(() => {
    fetchInvoices();

    // Fetch clients and products for creating invoices
    const fetchClientsAndProducts = async () => {
      try {
        // Fetch clients
        const clientsResponse = await axios.get('/api/admin/clients', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        // Fetch products
        const productsResponse = await axios.get('/api/products', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        setClients(clientsResponse.data);
        setFilteredClients(clientsResponse.data);
        setProducts(productsResponse.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load necessary data. Please refresh the page.');
      }
    };

    fetchClientsAndProducts();
  }, [pagination.page, filters]);

  // Recalculate totals whenever items change
  useEffect(() => {
    calculateTotals();
  }, [invoiceData.items]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });

      // Add filters
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.clientId) params.append('clientId', filters.clientId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.isPaid) params.append('isPaid', filters.isPaid);

      const response = await axios.get(`/api/invoices?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setInvoices(response.data.invoices);
      setPagination({
        ...pagination,
        totalPages: response.data.totalPages,
        total: response.data.total
      });
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError(err.response?.data?.error || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setPagination({ ...pagination, page: newPage });
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const applyFilters = () => {
    setPagination({ ...pagination, page: 1 }); // Reset to first page
    setShowFilters(false);
  };

  const resetFilters = () => {
    setFilters({
      status: 'all',
      search: '',
      clientId: '',
      startDate: '',
      endDate: '',
      isPaid: ''
    });
    setPagination({ ...pagination, page: 1 });
    setShowFilters(false);
  };

  const handleDeleteClick = (invoice) => {
    setConfirmDelete(invoice);
  };

  const handleDeleteInvoice = async () => {
    if (!confirmDelete) return;

    try {
      await axios.delete(`/api/invoices/${confirmDelete._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Remove the deleted invoice from state
      setInvoices(invoices.filter(invoice => invoice._id !== confirmDelete._id));
      setConfirmDelete(null);
      setNotification('Invoice deleted successfully');

      // Auto-hide notification after 3 seconds
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError(err.response?.data?.error || 'Failed to delete invoice');
    }
  };

  const handleShareInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    const clientInfo = clients.find(c => c._id === invoice.client)
    setShareEmail({
      to: invoice.client?.email || '',
      message: `Dear ${clientInfo?.firstName || invoice.client?.firstName || 'Client'},\n\nPlease find attached your invoice #${invoice.invoiceNumber} for $${invoice.totalAmount.toFixed(2)}. You can view and pay this invoice using the link below.\n\nThank you for your business!`
    });
    setShowShareDialog(true);
  };

  const copyToClipboard = (invoice) => {
    const paymentLink = `${window.location.origin}/invoice/pay/${invoice.paymentUrl}`;
    navigator.clipboard.writeText(paymentLink);
    setCopySuccess(true);

    // Reset after 2 seconds
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const calculateTotals = () => {
    const subtotal = invoiceData.items.reduce((sum, item) => {
      return sum + (item.unitPrice * item.quantity);
    }, 0);

    const gstTotal = invoiceData.items.reduce((sum, item) => {
      return sum + (item.hasGST ? item.unitPrice * item.quantity * 0.05 : 0);
    }, 0);

    const pstTotal = invoiceData.items.reduce((sum, item) => {
      return sum + (item.hasPST ? item.unitPrice * item.quantity * 0.07 : 0);
    }, 0);

    const totalAmount = subtotal + gstTotal + pstTotal - invoiceData.discountAmount;

    setInvoiceData({
      ...invoiceData,
      subtotal,
      gstTotal,
      pstTotal,
      totalAmount
    });
  };

  const handleClientSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    const filtered = clients.filter(client => {
      const searchString = `${client.firstName} ${client.lastName} ${client.email} ${client.company || ''}`.toLowerCase();
      return searchString.includes(term.toLowerCase());
    });

    setFilteredClients(filtered);
  };

  // const handleClientSelect = (client) => {
  //   setInvoiceData({
  //     ...invoiceData,
  //     client: client._id
  //   });
  //   setSearchTerm(`${client.firstName} ${client.lastName} - ${client.email}`);
  // };
  const handleClientSelect = (client) => {
    setInvoiceData({
      ...invoiceData,
      client: client._id
    });
    setSearchTerm(`${client.firstName} ${client.lastName} - ${client.email}`);
    // Close the dropdown by clearing filtered clients
    setFilteredClients([]);
  };

  const handleProductSelect = (index, productId) => {
    const product = products.find(p => p._id === productId);

    if (product) {
      const updatedItems = [...invoiceData.items];
      updatedItems[index] = {
        ...updatedItems[index],
        product: productId,
        unitPrice: product.basePrice,
        description: product.description || '',
        hasGST: product.hasGST,
        hasPST: product.hasPST
      };

      setInvoiceData({
        ...invoiceData,
        items: updatedItems
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle nested objects
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setInvoiceData({
        ...invoiceData,
        [parent]: {
          ...invoiceData[parent],
          [child]: value
        }
      });
    } else {
      setInvoiceData({
        ...invoiceData,
        [name]: value
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...invoiceData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    setInvoiceData({
      ...invoiceData,
      items: updatedItems
    });
  };

  const handleCheckboxChange = (index, field) => {
    const updatedItems = [...invoiceData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: !updatedItems[index][field]
    };

    setInvoiceData({
      ...invoiceData,
      items: updatedItems
    });
  };

  const addItem = () => {
    setInvoiceData({
      ...invoiceData,
      items: [
        ...invoiceData.items,
        { product: '', quantity: 1, unitPrice: 0, description: '', hasGST: false, hasPST: false }
      ]
    });
  };

  const removeItem = (index) => {
    const updatedItems = [...invoiceData.items];
    updatedItems.splice(index, 1);

    setInvoiceData({
      ...invoiceData,
      items: updatedItems
    });
  };

  const addCustomField = () => {
    setInvoiceData({
      ...invoiceData,
      customFields: [
        ...invoiceData.customFields,
        { fieldName: '', fieldValue: '', fieldType: 'text', isRequired: false }
      ]
    });
  };

  const handleCustomFieldChange = (index, field, value) => {
    const updatedFields = [...invoiceData.customFields];
    updatedFields[index] = {
      ...updatedFields[index],
      [field]: value
    };

    setInvoiceData({
      ...invoiceData,
      customFields: updatedFields
    });
  };

  const removeCustomField = (index) => {
    const updatedFields = [...invoiceData.customFields];
    updatedFields.splice(index, 1);

    setInvoiceData({
      ...invoiceData,
      customFields: updatedFields
    });
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();

    // Validation
    if (!invoiceData.client) {
      setError('Please select a client');
      return;
    }

    if (invoiceData.items.length === 0) {
      setError('Please add at least one item');
      return;
    }

    // Check if all required fields are filled
    for (const item of invoiceData.items) {
      if (!item.product) {
        setError('Please select a product for all items');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post('/api/invoices', invoiceData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setCreatedInvoice(response.data);
      setNotification('Invoice created successfully!');

      // Show share dialog
      setSelectedInvoice(response.data);

      // Prepare share message
      const clientInfo = clients.find(c => c._id === invoiceData.client);
      if (clientInfo) {
        setShareEmail({
          message: `Dear ${clientInfo.firstName},\n\nPlease find your invoice #${response.data.invoiceNumber} for $${response.data.totalAmount.toFixed(2)}. You can view and pay this invoice using the link below.\n\nThank you for your business!`
        });
      }

      setShowShareDialog(true);
      setShowCreateModal(false);

      // Refresh invoices list
      fetchInvoices();

      // Reset form
      setInvoiceData({
        client: '',
        dueDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
        items: [{ product: '', quantity: 1, unitPrice: 0, description: '', hasGST: false, hasPST: false }],
        customFields: [],
        notes: '',
        terms: 'Payment is due within 30 days of receipt.',
        subtotal: 0,
        gstTotal: 0,
        pstTotal: 0,
        discountAmount: 0,
        totalAmount: 0,
        coupon: null,
        companyInfo: {
          name: '',
          address: '',
          taxNumber: '',
          email: '',
          phone: ''
        }
      });
      setSearchTerm('');
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(err.response?.data?.error || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const sendInvoiceEmail = async () => {
    if (!selectedInvoice) return;

    try {
      setSendingShare(true);
      setShareError(null);

      await axios.post(`/api/invoices/${selectedInvoice._id}/send/email`, {
        message: shareEmail.message
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setShareSuccess(true);

      // Auto close dialog after success
      setTimeout(() => {
        setShowShareDialog(false);
        setShareSuccess(false);

        // Refresh invoices to update status
        fetchInvoices();
      }, 2000);
    } catch (err) {
      console.error('Error sending invoice email:', err);
      setShareError(err.response?.data?.error || 'Failed to send email');
    } finally {
      setSendingShare(false);
    }
  };

  const sendInvoiceSMS = async () => {
    if (!selectedInvoice) return;

    try {
      setSendingShare(true);
      setShareError(null);

      await axios.post(`/api/invoices/${selectedInvoice._id}/send/sms`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setShareSuccess(true);

      // Auto close dialog after success
      setTimeout(() => {
        setShowShareDialog(false);
        setShareSuccess(false);

        // Refresh invoices to update status
        fetchInvoices();
      }, 2000);
    } catch (err) {
      console.error('Error sending invoice SMS:', err);
      setShareError(err.response?.data?.error || 'Failed to send SMS');
    } finally {
      setSendingShare(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId, format) => {
    try {
      // Get the JWT token
      const token = localStorage.getItem('token');

      // Create the URL for the download endpoint
      const downloadUrl = `/api/invoices/${invoiceId}/download/${format}`;

      // Use axios to make the request with credentials and set responseType to 'blob'
      const response = await axios.get(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Determine the MIME type based on the format
      const mimeType = format === 'pdf' ? 'application/pdf' : 'text/csv';

      // Create a blob URL from the response data
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element and trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceId}.${format}`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error downloading invoice as ${format}:`, error);
      // Show an error notification to the user
    }
  };

  // const handleDownloadInvoice = async (invoiceId) => {
  //   try {
  //     // Get the JWT token
  //     const token = localStorage.getItem('token');

  //     // Create the URL for the download endpoint
  //     const downloadUrl = `/api/invoices/${invoiceId}/download`;

  //     // Use axios to make the request with credentials and set responseType to 'blob'
  //     const response = await axios.get(downloadUrl, {
  //       headers: { Authorization: `Bearer ${token}` },
  //       responseType: 'blob'
  //     });

  //     // Create a blob URL from the response data
  //     const blob = new Blob([response.data], { type: 'application/xml' });
  //     const url = window.URL.createObjectURL(blob);

  //     // Create a temporary link element and trigger the download
  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.setAttribute('download', `invoice-${invoiceId}.xml`);
  //     document.body.appendChild(link);
  //     link.click();

  //     // Clean up
  //     link.parentNode.removeChild(link);
  //     window.URL.revokeObjectURL(url);
  //   } catch (error) {
  //     console.error('Error downloading invoice:', error);
  //     // Show an error notification to the user
  //     // (Using whatever notification system your app is using)
  //   }
  // };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <span className="px-2 py-1 bg-gray-200 text-gray-800 rounded-full text-xs">Draft</span>;
      case 'sent':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">Sent</span>;
      case 'viewed':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">Viewed</span>;
      case 'paid':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Paid</span>;
      case 'overdue':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">Overdue</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">Cancelled</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">{status}</span>;
    }
  };

  if (loading && invoices.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading invoices...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 container ">

      {/* Header */}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Invoice Management</h1>

        <Button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center bg-green-500 hover:bg-green-600"
        >
          <Plus size={18} className="mr-1" />Create Invoice
        </Button>
      </div>

      {notification && (
        <Alert className="mb-4 bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertDescription className="ml-2 text-green-700">{notification}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="ml-2">{error}</AlertDescription>
        </Alert>
      )}

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Filters and Search */}
        <div className="p-4 border-b flex flex-col md:flex-row justify-between gap-4">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by invoice number, client name, or amount"
              value={filters.search}
              onChange={(e) => handleFilterChange({ target: { name: 'search', value: e.target.value } })}
              className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex space-x-2">
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="px-8 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="viewed">Viewed</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center"
            >
              <Filter size={18} className="mr-1" /> Filters
            </Button>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="p-4 bg-gray-50 border-b">
            <h3 className="font-medium mb-3">Advanced Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Payment Status</label>
                <select
                  name="isPaid"
                  value={filters.isPaid}
                  onChange={handleFilterChange}
                  className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All</option>
                  <option value="true">Paid</option>
                  <option value="false">Unpaid</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end mt-4 space-x-2">
              <Button
                variant="outline"
                onClick={resetFilters}
              >
                Reset
              </Button>
              <Button onClick={applyFilters}>
                Apply Filters
              </Button>
            </div>
          </div>
        )}

        {/* Invoices Table */}
        {/* <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice #
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    No invoices found
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => (
                  <tr key={invoice._id} className="hover:bg-gray-50">                   
                    {/* <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleDownloadInvoice(invoice._id)}
                          style={{ 
                            boxShadow: 'none', border: 'none', transform: 'none',     
                          }}
                          className="text-blue-600 hover:text-blue-900 hover:underline"
                        >
                          {invoice.invoiceNumber}
                        </button>
                        <Download 
                          size={16} 
                          className="text-gray-500 cursor-pointer" 
                          onClick={() => handleDownloadInvoice(invoice._id)}
                        />
                      </div>
                    </td> 
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center">
                        <span className="text-blue-600">{invoice.invoiceNumber}</span>
                        <div className="ml-2 flex space-x-1">
                          <Download 
                            size={16} 
                            className="text-blue-500 cursor-pointer hover:text-blue-700" 
                            onClick={() => handleDownloadInvoice(invoice._id, 'pdf')}
                            title="Download as PDF"
                          />
                          <FileText 
                            size={16} 
                            className="text-green-500 cursor-pointer hover:text-green-700" 
                            onClick={() => handleDownloadInvoice(invoice._id, 'csv')}
                            title="Download as CSV"
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {invoice.client?.firstName} {invoice.client?.lastName}
                      <div className="text-xs text-gray-500">{invoice.client?.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(invoice.issueDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(invoice.dueDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      ${invoice.totalAmount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(invoice.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleShareInvoice(invoice)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Share Invoice"
                          style={{ 
                            boxShadow: 'none', border: 'none', transform: 'none',     
                          }}
                        >
                          <Mail size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(invoice)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Invoice"
                          disabled={invoice.status === 'paid'}
                          style={{ 
                            boxShadow: 'none', border: 'none', transform: 'none',     
                          }}
                        >
                          <Trash size={18} className={invoice.status === 'paid' ? 'opacity-50 cursor-not-allowed' : ''} />
                        </button>
                        <a
                          href={`/invoice/pay/${invoice.paymentUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-600 hover:text-gray-900"
                          title="View Payment Page"
                        >
                          <ExternalLink size={18} />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div> */}
        {/* Invoices Table */}
        <div className="overflow-x-auto">
          {/* Table for larger screens (hidden on small screens) */}
          <div className="hidden xl:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => (
                    <tr key={invoice._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center">
                          <span className="text-blue-600">{invoice.invoiceNumber}</span>
                          <div className="ml-2 flex space-x-1">
                            <Download
                              size={16}
                              className="text-blue-500 cursor-pointer hover:text-blue-700"
                              onClick={() => handleDownloadInvoice(invoice._id, 'pdf')}
                              title="Download as PDF"
                            />
                            <FileText
                              size={16}
                              className="text-green-500 cursor-pointer hover:text-green-700"
                              onClick={() => handleDownloadInvoice(invoice._id, 'csv')}
                              title="Download as CSV"
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {invoice.client?.firstName} {invoice.client?.lastName}
                        <div className="text-xs text-gray-500">{invoice.client?.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(invoice.issueDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(invoice.dueDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        ${invoice.totalAmount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(invoice.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleShareInvoice(invoice)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Share Invoice"
                            style={{
                              boxShadow: 'none', border: 'none', transform: 'none',
                            }}
                          >
                            <Mail size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(invoice)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Invoice"
                            disabled={invoice.status === 'paid'}
                            style={{
                              boxShadow: 'none', border: 'none', transform: 'none',
                            }}
                          >
                            <Trash size={18} className={invoice.status === 'paid' ? 'opacity-50 cursor-not-allowed' : ''} />
                          </button>
                          <a
                            href={`/invoice/pay/${invoice.paymentUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-gray-900"
                            title="View Payment Page"
                          >
                            <ExternalLink size={18} />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Card layout for mobile/small screens */}
          <div className="xl:hidden">
            {invoices.length === 0 ? (
              <div className="py-4 text-center text-gray-500">
                No invoices found
              </div>
            ) : (
              <div className="space-y-4 px-4">
                {invoices.map((invoice) => (
                  <div
                    key={invoice._id}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm p-4"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center mb-1">
                          <span className="text-blue-600 font-medium">{invoice.invoiceNumber}</span>
                          <div className="ml-2 flex space-x-1">
                            <Download
                              size={16}
                              className="text-blue-500 cursor-pointer hover:text-blue-700"
                              onClick={() => handleDownloadInvoice(invoice._id, 'pdf')}
                              title="Download as PDF"
                            />
                            <FileText
                              size={16}
                              className="text-green-500 cursor-pointer hover:text-green-700"
                              onClick={() => handleDownloadInvoice(invoice._id, 'csv')}
                              title="Download as CSV"
                            />
                          </div>
                        </div>
                        {getStatusBadge(invoice.status)}
                      </div>
                      <div className="font-bold text-lg">${invoice.totalAmount.toFixed(2)}</div>
                    </div>

                    <div className="mb-2">
                      <div className="font-medium">{invoice.client?.firstName} {invoice.client?.lastName}</div>
                      <div className="text-sm text-gray-500">{invoice.client?.email}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-1 text-sm text-gray-500 mb-3">
                      <div>
                        <span className="block text-xs uppercase text-gray-400">Issue Date</span>
                        {new Date(invoice.issueDate).toLocaleDateString()}
                      </div>
                      <div>
                        <span className="block text-xs uppercase text-gray-400">Due Date</span>
                        {new Date(invoice.dueDate).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="flex justify-between pt-2 border-t border-gray-100">
                      <button
                        onClick={() => handleShareInvoice(invoice)}
                        className="text-blue-600 hover:text-blue-900 p-2 rounded-full hover:bg-blue-50"
                        title="Share Invoice"
                        style={{
                          boxShadow: 'none', border: 'none', transform: 'none',
                        }}
                      >
                        <Mail size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(invoice)}
                        className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50"
                        title="Delete Invoice"
                        disabled={invoice.status === 'paid'}
                        style={{
                          boxShadow: 'none', border: 'none', transform: 'none',
                        }}
                      >
                        <Trash size={18} className={invoice.status === 'paid' ? 'opacity-50 cursor-not-allowed' : ''} />
                      </button>
                      <a
                        href={`/invoice/pay/${invoice.paymentUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-gray-900 p-2 rounded-full hover:bg-gray-50"
                        title="View Payment Page"
                      >
                        <ExternalLink size={18} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 flex items-center justify-between border-t">
            <div className="flex-1 flex justify-between items-center">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> invoices
              </div>

              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="flex items-center"
                >
                  <ChevronLeft size={16} className="mr-1" /> Previous
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="flex items-center"
                >
                  Next <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      {/* <Dialog open={showCreateModal} onOpenChange={setShowCreateModal} className="min-w-5xl" >
        <DialogContent className="min-w-4xl sm:w-[95vw] xl:w-[50vw] max-w-[90vw]" >
          <DialogHeader>
            <DialogTitle>Create New Invoice</DialogTitle>
          </DialogHeader>
          
          <div className="max-h-[70vh] overflow-y-auto p-4"> */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[90vw] lg:max-w-[70vw] xl:max-w-[60vw] max-h-[90vh] overflow-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Create New Invoice</DialogTitle>
            <button
              style={{ boxShadow: 'none', border: 'none', transform: 'none' }}
              onClick={() => setShowCreateModal(false)}
              className="rounded-full p-1 hover:bg-gray-200"
            >
              <X size={18} />
            </button>
          </DialogHeader>

          <div className="max-h-[70vh] overflow-y-auto p-4">

            <form onSubmit={handleCreateInvoice} className="space-y-6">
              {/* Client Selection */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <User className="mr-2 h-5 w-5 text-blue-500" />
                  Client Information
                </h2>

                <div className="mb-4 relative">
                  <label className="block text-sm font-medium mb-1">Select Client*</label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleClientSearch}
                    placeholder="Search clients by name, email, or company"
                    className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                  />

                  {searchTerm && filteredClients.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredClients.map(client => (
                        <div
                          key={client._id}
                          onClick={() => handleClientSelect(client)}
                          className="p-2 hover:bg-gray-100 cursor-pointer"
                        >
                          <div className="font-medium">{client.firstName} {client.lastName}</div>
                          <div className="text-sm text-gray-600">{client.email}</div>
                          {client.company && <div className="text-sm text-gray-500">{client.company}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Due Date*</label>
                  <input
                    type="date"
                    name="dueDate"
                    value={invoiceData.dueDate}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              {/* Company Information */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <DollarSign className="mr-2 h-5 w-5 text-blue-500" />
                  Company Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Company Name</label>
                    <input
                      type="text"
                      name="companyInfo.name"
                      value={invoiceData.companyInfo.name}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Tax Number</label>
                    <input
                      type="text"
                      name="companyInfo.taxNumber"
                      value={invoiceData.companyInfo.taxNumber}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Tax/GST Number"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Company Address</label>
                    <input
                      type="text"
                      name="companyInfo.address"
                      value={invoiceData.companyInfo.address}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your company address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Contact Email</label>
                    <input
                      type="email"
                      name="companyInfo.email"
                      value={invoiceData.companyInfo.email}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                      placeholder="contact@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Contact Phone</label>
                    <input
                      type="text"
                      name="companyInfo.phone"
                      value={invoiceData.companyInfo.phone}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+1 (555) 555-5555"
                    />
                  </div>
                </div>
              </div>

              {/* Invoice Items */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold flex items-center">
                    <Tag className="mr-2 h-5 w-5 text-blue-500" />
                    Invoice Items
                  </h2>

                  <Button
                    type="button"
                    onClick={addItem}
                    className="flex items-center text-sm"
                    variant="outline"
                  >
                    <Plus size={16} className="mr-1" /> Add Item
                  </Button>
                </div>

                {invoiceData.items.map((item, index) => (
                  <div key={index} className="mb-4 p-4 border rounded-lg relative">
                    {invoiceData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                      >
                        <Trash size={18} />
                      </button>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-1">Product*</label>
                        <select
                          value={item.product}
                          onChange={(e) => handleProductSelect(index, e.target.value)}
                          className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          <option value="">Select a product</option>
                          {products.map(product => (
                            <option key={product._id} value={product._id}>
                              {product.name} - ${product.basePrice.toFixed(2)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Quantity*</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                          min="1"
                          className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Unit Price*</label>
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-2">
                      <label className="block text-sm font-medium mb-1">Description</label>
                      <textarea
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                        rows="2"
                      ></textarea>
                    </div>

                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <Checkbox
                          checked={item.hasGST}
                          onCheckedChange={() => handleCheckboxChange(index, 'hasGST')}
                          className="mr-2"
                        />
                        <span className="text-sm">Apply GST (5%)</span>
                      </label>

                      <label className="flex items-center">
                        <Checkbox
                          checked={item.hasPST}
                          onCheckedChange={() => handleCheckboxChange(index, 'hasPST')}
                          className="mr-2"
                        />
                        <span className="text-sm">Apply PST (7%)</span>
                      </label>
                    </div>

                    <div className="text-right mt-2 text-sm text-gray-600">
                      Item Total: ${(item.unitPrice * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}

                <div className="flex justify-end space-y-2">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>${invoiceData.subtotal.toFixed(2)}</span>
                    </div>

                    {invoiceData.gstTotal > 0 && (
                      <div className="flex justify-between">
                        <span>GST (5%):</span>
                        <span>${invoiceData.gstTotal.toFixed(2)}</span>
                      </div>
                    )}

                    {invoiceData.pstTotal > 0 && (
                      <div className="flex justify-between">
                        <span>PST (7%):</span>
                        <span>${invoiceData.pstTotal.toFixed(2)}</span>
                      </div>
                    )}

                    <div className="flex justify-between">
                      <span>Discount:</span>
                      <div className="flex items-center">
                        <span>$</span>
                        <input
                          type="number"
                          value={invoiceData.discountAmount}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            setInvoiceData({
                              ...invoiceData,
                              discountAmount: value,
                              totalAmount: invoiceData.subtotal + invoiceData.gstTotal + invoiceData.pstTotal - value
                            });
                          }}
                          min="0"
                          step="0.01"
                          className="w-20 p-1 border rounded ml-1"
                        />
                      </div>
                    </div>

                    <div className="flex justify-between font-bold border-t pt-2">
                      <span>Total:</span>
                      <span>${invoiceData.totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes & Terms */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h2 className="text-lg font-semibold mb-4">Notes & Terms</h2>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
                  <textarea
                    name="notes"
                    value={invoiceData.notes}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Additional notes to appear on the invoice"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Terms & Conditions</label>
                  <textarea
                    name="terms"
                    value={invoiceData.terms}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Terms and conditions for this invoice"
                  ></textarea>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  {loading ? 'Creating...' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      {showShareDialog && selectedInvoice && (
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent>
            <DialogHeader className="flex flex-row items-center justify-between">
              <DialogTitle>Share Invoice #{selectedInvoice.invoiceNumber}</DialogTitle>
              <button
                style={{ boxShadow: 'none', border: 'none', transform: 'none' }}
                onClick={() => setShowShareDialog(false)}
                className="rounded-full p-1 hover:bg-gray-200"
              >
                <X size={18} />
              </button>
            </DialogHeader>

            <div className="p-4">
              <div className="mb-4">
                <h3 className="font-medium mb-2">Payment Link</h3>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={`${window.location.origin}/invoice/pay/${selectedInvoice.paymentUrl}`}
                    readOnly
                    className="flex-1 p-2 border rounded-l focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => copyToClipboard(selectedInvoice)}
                    className={`p-2 ${copySuccess ? 'bg-green-500' : 'bg-blue-500'} text-white rounded-r`}
                  >
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium mb-2">Send via Email</h3>
                <textarea
                  value={shareEmail.message}
                  onChange={(e) => setShareEmail({ ...shareEmail, message: e.target.value })}
                  className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500 mb-2"
                  rows="5"
                  placeholder="Enter your message"
                ></textarea>

                <p className="text-sm text-gray-500 mb-4">
                  Will be sent to: {selectedInvoice.client?.email || 'No email available'}
                </p>

                <Button
                  onClick={sendInvoiceEmail}
                  disabled={!selectedInvoice.client?.email || sendingShare}
                  className="w-full mb-4"
                >
                  {sendingShare ? 'Sending...' : 'Send Email'}
                </Button>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium mb-2">Send via SMS</h3>
                <p className="text-sm mb-2">
                  A brief SMS with the payment link will be sent to the client's phone number.
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Phone: {selectedInvoice.client?.phone || 'No phone number available'}
                </p>

                <Button
                  onClick={sendInvoiceSMS}
                  disabled={!selectedInvoice.client?.phone || sendingShare}
                  className="w-full"
                >
                  {sendingShare ? 'Sending...' : 'Send SMS'}
                </Button>
              </div>

              {shareError && (
                <Alert variant="destructive" className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="ml-2">{shareError}</AlertDescription>
                </Alert>
              )}

              {shareSuccess && (
                <Alert className="mt-4 bg-green-50 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <AlertDescription className="ml-2 text-green-700">
                    Sent successfully!
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {confirmDelete && (
        <Dialog open={Boolean(confirmDelete)} onOpenChange={() => setConfirmDelete(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Delete</DialogTitle>
            </DialogHeader>

            <div className="p-4">
              <p className="mb-4">Are you sure you want to delete invoice #{confirmDelete.invoiceNumber}?</p>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(null)}
                >
                  Cancel
                </Button>

                <Button
                  variant="destructive"
                  onClick={handleDeleteInvoice}
                >
                  Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default InvoiceManagement;


